// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SparseAsset } from '@/asset/api/types';
import { AssetCreationUploadResult } from '@/asset/api/upload-asset-creation';
import type { ExpandedAssetsState } from '@/asset/components/asset-creation-upload/types';
import { Link } from '@/components/link';
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: AssetCreationUploadResult | null;
  expandedAssets: ExpandedAssetsState;
  onToggleExpanded: (section: keyof ExpandedAssetsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  serial: string;
  errors: string[];
}

interface CreatedAssetRowProps {
  index: number;
  style: React.CSSProperties;
  data: SparseAsset[];
}

const CreatedAssetRow = ({ index, style, data }: CreatedAssetRowProps) => {
  const { serial, key } = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link to={`/assets/$assetKey`} params={{ assetKey: key }}>
              {serial}
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.serial ? ` (serial "${row.serial}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
        />
      </ListItem>
    </div>
  );
};

export function Results({
  uploadResult,
  expandedAssets,
  onToggleExpanded,
  onBack,
  onStartNew,
  onClose,
}: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  const processedData = useMemo(() => {
    if (!uploadResult) {
      return {
        assetsCreated: [],
        failedRows: [],
      };
    }

    const assetsCreated: SparseAsset[] = [];
    const failedRows: RowFailure[] = [];

    // Process items (successful creations and item-level errors)
    uploadResult.items.forEach((item) => {
      if (item.errors.length > 0) {
        failedRows.push({
          rowNumber: item.index,
          serial: item.serial,
          errors: item.errors.map(
            (err) => `Column "${err.fieldName}": ${err.error}`
          ),
        });
        return;
      }
      if (item.created && item.validatedAsset) {
        assetsCreated.push(item.validatedAsset);
      }
    });

    // Process rowErrors (validation errors grouped by row)
    // Multiple rowError entries can exist for the same index, so we consolidate them
    const rowErrorMap = new Map<number, string[]>();
    uploadResult.rowErrors.forEach((rowError) => {
      const existing = rowErrorMap.get(rowError.index) ?? [];
      const newErrors = rowError.errors.map(
        (err) => `Column "${err.fieldName}": ${err.error}`
      );
      rowErrorMap.set(rowError.index, [...existing, ...newErrors]);
    });

    // Convert consolidated row errors to RowFailure format
    rowErrorMap.forEach((errors, rowNumber) => {
      failedRows.push({
        rowNumber,
        serial: '', // RowErrors don't include serial
        errors,
      });
    });

    // Sort failed rows by row number
    failedRows.sort((a, b) => a.rowNumber - b.rowNumber);

    return { assetsCreated, failedRows };
  }, [uploadResult]);

  const { assetsCreated, failedRows } = processedData;

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking copy
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'serial', 'serial')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  if (!uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, structureErrors, rowErrors } = uploadResult;

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Successfully created{' '}
                {pluralize(assetsCreated.length, {
                  singular: 'asset',
                  plural: 'assets',
                })}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                  cursor: assetsCreated.length ? 'pointer' : undefined,
                }}
                onClick={
                  assetsCreated.length
                    ? () => onToggleExpanded('created')
                    : undefined
                }
              >
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Assets created ({formatNumber(assetsCreated.length)})
                  </Typography>
                </Box>
                {assetsCreated.length > 0 &&
                  (expandedAssets.created ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  ))}
              </Box>
              <Collapse in={expandedAssets.created}>
                <VirtualList
                  height={Math.min(assetsCreated.length * ITEM_HEIGHT, 200)}
                  itemCount={assetsCreated.length}
                  itemSize={ITEM_HEIGHT}
                  itemData={assetsCreated}
                  width="100%"
                >
                  {CreatedAssetRow}
                </VirtualList>
              </Collapse>
            </Paper>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required asset creation fields
                  </Typography>
                </>
              )}
              {(failedRows.length > 0 || rowErrors.length > 0) && (
                <>
                  <AlertTitle>Asset creation failed</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that could not be used to
                    create assets
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {expandedAssets.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={expandedAssets.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column &quot;{`${row.fieldName}`}&quot;: {row.error}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'asset',
                        plural: 'assets',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {expandedAssets.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={expandedAssets.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Box>
            <Button variant="outlined" onClick={onStartNew} sx={{ mr: 1 }}>
              Start new upload
            </Button>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
